import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsTenantService } from "@/services/analytics-tenant-service";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import { tenantAdminService } from "@/services/tenant-admin-service";
import type { OverviewKpiMetric } from "@/components/pages/analytics/front-desk/components/overview/overviewKpiMappers";
import {
  buildOverviewMetricDelta,
  formatOrdersValue,
  formatPercentValue,
  formatRatingValue,
  formatRevenueValue,
} from "@/components/pages/analytics/front-desk/components/overview/overviewKpiMappers";
import { toAnalyticsDateParam } from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import {
  mapTenantOverviewToAreaChartData,
  type OverviewAreaChartPoint,
} from "@/components/pages/analytics/front-desk/components/overview/overviewAreaChartMappers";
import { mapTenantTablesToAdminOverviewRowsWithComparison } from "../utils/adminOverviewMappers";
import type { AdminHotelOverviewRow } from "../types/adminOverview";
import type { AdminMetricCell } from "../types/adminHotelMetricTable";
import { useAdminTenantId } from "./useAdminTenantId";
import { getPreviousAnalyticsDateRange } from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";

function isDateRangeReady(dateRange: DateRange): boolean {
  return Boolean(
    toAnalyticsDateParam(dateRange.from) && toAnalyticsDateParam(dateRange.to),
  );
}

async function fetchAdminOverviewData(
  tenantId: string,
  from: string,
  to: string,
  previousFrom: string,
  previousTo: string,
) {
  const rangeQuery = { from, to };
  const prevRangeQuery = { from: previousFrom, to: previousTo };

  const [
    overview,
    revenueTable,
    offersTable,
    reviewsTable,
    servicesTable,
    previousRevenueTable,
    previousOffersTable,
    previousReviewsTable,
    previousServicesTable,
    hotelOptions,
    tenant,
  ] = await Promise.all([
    analyticsTenantService.getOverview(rangeQuery),
    analyticsTenantService.getRevenueTable(rangeQuery),
    analyticsTenantService.getOffersTable(rangeQuery),
    analyticsTenantService.getReviewsTable(rangeQuery),
    analyticsTenantService.getServicesTable(rangeQuery),
    analyticsTenantService.getRevenueTable(prevRangeQuery),
    analyticsTenantService.getOffersTable(prevRangeQuery),
    analyticsTenantService.getReviewsTable(prevRangeQuery),
    analyticsTenantService.getServicesTable(prevRangeQuery),
    partnerDashboardService
      .getHotelFilterOptions(tenantId)
      .catch(
        () =>
          [] as Awaited<
            ReturnType<typeof partnerDashboardService.getHotelFilterOptions>
          >,
      ),
    tenantAdminService.getTenant(tenantId),
  ]);

  const hotelLogosById = Object.fromEntries(
    hotelOptions.map((hotel) => [hotel.id, hotel.logoId]),
  );

  const rows = mapTenantTablesToAdminOverviewRowsWithComparison({
    revenueRows: revenueTable.rows,
    offersRows: offersTable.rows,
    reviewsRows: reviewsTable.rows,
    servicesRows: servicesTable.rows,

    previousRevenueRows: previousRevenueTable.rows,
    previousOffersRows: previousOffersTable.rows,
    previousReviewsRows: previousReviewsTable.rows,
    previousServicesRows: previousServicesTable.rows,

    hotelLogosById,
  });

  const totalOrders = servicesTable.rows.reduce(
    (sum, row) => sum + row.orderCount,
    0,
  );
  const previousTotalOrders = previousServicesTable.rows.reduce(
    (sum, row) => sum + row.orderCount,
    0,
  );

  return {
    overview,
    rows,
    tenantName: tenant.name,
    totalOrders,
    previousTotalOrders,
  };
}

export const adminOverviewKeys = {
  all: ["admin-analytics-overview"] as const,
  hotels: (tenantId: string, from: string, to: string) =>
    [...adminOverviewKeys.all, tenantId, from, to] as const,
};

export function useAdminOverviewHotels(dateRange: DateRange) {
  const tenantId = useAdminTenantId();
  const from = toAnalyticsDateParam(dateRange.from) ?? "";
  const to = toAnalyticsDateParam(dateRange.to) ?? "";
  const previousRange = getPreviousAnalyticsDateRange(dateRange);
  const previousFrom = toAnalyticsDateParam(previousRange?.from) ?? "";
  const previousTo = toAnalyticsDateParam(previousRange?.to) ?? "";
  const isReady = Boolean(
    tenantId && isDateRangeReady(dateRange) && previousFrom && previousTo,
  );

  const query = useQuery({
    queryKey: adminOverviewKeys.hotels(tenantId ?? "", from, to),
    queryFn: () =>
      fetchAdminOverviewData(tenantId!, from, to, previousFrom, previousTo),
    enabled: isReady,
  });

  const amountDivisor = query.data?.overview.amountDivisor ?? 1;

  const networkMetrics = useMemo<Record<string, AdminMetricCell> | null>(() => {
    if (!query.data?.overview) {
      return null;
    }

    const { kpis } = query.data.overview;
    const totalRevenue = (kpis.totalRevenue.current ?? 0) / amountDivisor;

    return {
      serviceRevenue: {
        value: formatRevenueValue(totalRevenue),
        ...buildOverviewMetricDelta(
          kpis.totalRevenue.current,
          kpis.totalRevenue.previous,
        ),
      },
      conversionOfOffers: {
        value: formatPercentValue(kpis.offerConversion.current ?? 0),
        ...buildOverviewMetricDelta(
          kpis.offerConversion.current,
          kpis.offerConversion.previous,
        ),
      },
      avgGuestRating: {
        value: formatRatingValue(kpis.avgRating.current ?? 0),
        ...buildOverviewMetricDelta(
          kpis.avgRating.current,
          kpis.avgRating.previous,
        ),
      },
      ordersForStay: {
        value: formatOrdersValue(query.data.totalOrders),
        ...buildOverviewMetricDelta(
          query.data.totalOrders,
          query.data.previousTotalOrders,
        ),
      },
    };
  }, [amountDivisor, query.data]);

  const kpiMetrics = useMemo<OverviewKpiMetric[]>(() => {
    if (!query.data?.overview) {
      return [];
    }

    const { kpis } = query.data.overview;
    const totalRevenue = (kpis.totalRevenue.current ?? 0) / amountDivisor;

    return [
      {
        title: "Service revenue",
        value: formatRevenueValue(totalRevenue),
        ...buildOverviewMetricDelta(
          kpis.totalRevenue.current,
          kpis.totalRevenue.previous,
        ),
      },
      {
        title: "Conversion of offers",
        value: formatPercentValue(kpis.offerConversion.current ?? 0),
        ...buildOverviewMetricDelta(
          kpis.offerConversion.current,
          kpis.offerConversion.previous,
        ),
      },
      {
        title: "Avg. Guest Rating for Stay",
        value: formatRatingValue(kpis.avgRating.current ?? 0),
        ...buildOverviewMetricDelta(
          kpis.avgRating.current,
          kpis.avgRating.previous,
        ),
      },
      {
        title: "Orders for stay",
        value: formatOrdersValue(kpis.ordersPerStay.current ?? 0),
        ...buildOverviewMetricDelta(
          kpis.ordersPerStay.current,
          kpis.ordersPerStay.previous,
        ),
      },
    ];
  }, [amountDivisor, query.data?.overview]);

  const chartData = useMemo<OverviewAreaChartPoint[]>(
    () => mapTenantOverviewToAreaChartData(query.data?.overview),
    [query.data?.overview],
  );

  const rows = useMemo<AdminHotelOverviewRow[]>(
    () => query.data?.rows ?? [],
    [query.data?.rows],
  );

  return {
    tenantId,
    tenantName: query.data?.tenantName ?? "",
    rows,
    networkMetrics,
    kpiMetrics,
    chartData,
    isLoading: query.isLoading,
    isError: query.isError,
    isReady,
  };
}
